import React from 'react';
import {
  Box, Button, ButtonText, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import { CheckCircle, MonitorStop } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { AiExaminerPresence } from './AiExaminerPresence';
import { OralExamPracticeBanner } from './OralExamPracticeBanner';

export type ExamWrapUpProps = {
  state: 'completed' | 'aborted' | 'closed' | 'not_started';
  studentName?: string | null;
  recordingRequired?: boolean;
  /** When false, wrap-up says the run is not graded (dry run). Default true. */
  requiresEvaluation?: boolean;
  /** When false, omits the AI examiner orb (1:1 wrap-up). Default true for 1:N. */
  showPresence?: boolean;
  onDone?: () => void;
};

/** Post-exam wrap-up for completed, aborted, or normally-closed runs (§3.7). */
export function ExamWrapUp({
  state,
  studentName,
  recordingRequired,
  requiresEvaluation = true,
  showPresence = true,
  onDone,
}: ExamWrapUpProps) {
  const { t } = useTranslation();
  const isCompleted = state === 'completed';
  const isClosed = state === 'closed';
  const isNotStarted = state === 'not_started';
  const showRecordingCopy = recordingRequired !== false && requiresEvaluation;
  const notGraded = requiresEvaluation === false;

  const titleKey = isCompleted
    ? 'oralExam.wrapUp.completed.title'
    : isClosed
      ? 'oralExam.wrapUp.closed.title'
      : isNotStarted
        ? 'oralExam.wrapUp.notStarted.title'
        : 'oralExam.wrapUp.aborted.title';
  const titleDefault = isCompleted
    ? (studentName
      ? t('oralExam.wrapUp.completed.titlePersonalized', {
        name: studentName,
        defaultValue: `You're all done, ${studentName}.`,
      })
      : "You're all done")
    : isClosed
      ? 'The exam session has ended.'
      : isNotStarted
        ? "This exam didn't start"
        : 'This session was ended';
  const bodyKey = isCompleted
    ? 'oralExam.wrapUp.completed.body'
    : isClosed
      ? 'oralExam.wrapUp.closed.body'
      : isNotStarted
        ? 'oralExam.wrapUp.notStarted.body'
        : 'oralExam.wrapUp.aborted.body';
  const bodyDefault = isCompleted
    ? (notGraded
      ? 'Thank you. This was a dry run — nothing was graded and no record was kept.'
      : (showRecordingCopy
        ? "Thank you. Your answers and recording have been submitted. Your supervisor will grade them and you'll be notified when results are available."
        : "Thank you. Your answers have been submitted. Your supervisor will grade them and you'll be notified when results are available."))
    : isClosed
      ? 'Thank you for joining. Your supervisor will follow up if anything else is needed.'
      : isNotStarted
        ? 'You left before the exam began. You can book another slot for this exam from the module.'
        : 'Your supervisor will follow up with next steps.';

  return (
    <VStack
      flex={1}
      p="$5"
      justifyContent="center"
      alignItems="center"
      testID="oral-exam-student-terminal"
    >
      <VStack
        space="lg"
        alignItems="center"
        maxWidth={480}
        testID="oral-exam-wrapup"
      >
        {notGraded ? <OralExamPracticeBanner /> : null}
        {showPresence ? <AiExaminerPresence /> : null}

        {isCompleted ? (
          <Box
            w={40}
            h={40}
            borderRadius="$full"
            borderWidth={2}
            borderColor="$statusSuccess"
            alignItems="center"
            justifyContent="center"
            testID="oral-exam-wrapup-checkmark"
          >
            <Icon as={CheckCircle} size="md" color="$statusSuccess" />
          </Box>
        ) : null}

        <Text
          size="3xl"
          fontWeight="bold"
          color="$textHeading"
          textAlign="center"
          testID="oral-exam-wrapup-title"
        >
          {studentName && isCompleted
            ? t('oralExam.wrapUp.completed.titlePersonalized', {
              name: studentName,
              defaultValue: `You're all done, ${studentName}.`,
            })
            : t(titleKey, { defaultValue: titleDefault })}
        </Text>

        <Text size="md" color="$textBody" textAlign="center" lineHeight={24}>
          {isCompleted && notGraded
            ? t('oralExam.practice.wrapUp.body', { defaultValue: bodyDefault })
            : isCompleted
              ? (showRecordingCopy
                ? t(bodyKey, { defaultValue: bodyDefault })
                : t('oralExam.wrapUp.completed.bodyNoRecording', { defaultValue: bodyDefault }))
              : t(bodyKey, { defaultValue: bodyDefault })}
        </Text>

        {isCompleted && showRecordingCopy ? (
          <VStack
            space="xs"
            alignItems="center"
            width="$full"
            testID="oral-exam-wrapup-notice"
          >
            <HStack space="sm" alignItems="center" justifyContent="center">
              <Icon as={MonitorStop} size="xs" color="$textSubtle" />
              <Text size="sm" color="$textSubtle">
                {t('oralExam.wrapUp.completed.bulletRecording', {
                  defaultValue: 'Recording has stopped automatically.',
                })}
              </Text>
            </HStack>
          </VStack>
        ) : null}

        {onDone ? (
          <Button
            size="lg"
            action="primary"
            width="$full"
            onPress={onDone}
            testID="oral-exam-wrapup-done"
          >
            <ButtonText>{t('common.done', { defaultValue: 'Done' })}</ButtonText>
          </Button>
        ) : null}
      </VStack>
    </VStack>
  );
}
